import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { EyeIcon } from 'lucide-react';
import { TrophyGlyph } from '../components/icons/BeelyvIcons';
import { Avatar } from '../components/Avatar';
import { MetricCard } from '../components/MetricCard';
import { PageHeader } from '../components/PageHeader';
import { Button } from '../components/ui/Button';
import { Card, SectionTitle } from '../components/ui/Card';
import { biomarkerDefs } from '../data/biomarkers';
import { people } from '../data/people';
import { metricGroupsForSport, sports, standaloneMetrics } from '../data/sportMetrics';
import { profileFieldsForSport } from '../data/sportProfileFields';
import { displaySportField, formatMetricGroup } from '../utils/sportFields';
import { currentUser } from '../data/user';
import { useBiomarkers } from '../contexts/BiomarkersContext';
import { useLocale } from '../contexts/LocaleContext';
import { useMetrics } from '../contexts/MetricsContext';
import { useProgress } from '../contexts/ProgressContext';
import { useSettings } from '../contexts/SettingsContext';
import { average, buildHistory } from '../utils/history';
import { convertLabValue, formatLabValue, formatMetric } from '../utils/units';

/**
 * MOB/Profile/PublicPreview — the logged user's profile exactly as others see
 * it. Every section is driven by an explicit visibility toggle.
 */
export function OwnPublicProfile() {
  const {
    t,
    tl,
    locale
  } = useLocale();
  const navigate = useNavigate();
  const metrics = useMetrics();
  const progress = useProgress();
  const bio = useBiomarkers();
  const {
    privacy
  } = useSettings();
  const me = people[0];
  const showBadges = privacy.publicAchievements && privacy.publicBadges;
  const showStreak = privacy.publicStreak;
  const history = useMemo(() => buildHistory(30, currentUser.streak), []);

  // Numeric metrics + categorical profile fields the user explicitly made public.
  const sportPublic: {
    id: string;
    label: string;
    value: string;
  }[] = privacy.publicSportMetrics ? sports.flatMap(sport => [...standaloneMetrics(sport).filter(metric => metric.value > 0 && metrics.isPublic(metric.id)).map(metric => ({
    id: metric.id,
    label: tl(metric.label),
    value: formatMetric(metric.kind, metric.value, metrics.unitSystem)
  })), ...metricGroupsForSport(sport).filter(group => metrics.isPublic(group.id) && group.members.some(member => member.value > 0)).map(group => ({
    id: group.id,
    label: tl(group.label),
    value: formatMetricGroup(group.members.map(member => member.id), Object.fromEntries(group.members.map(member => [member.id, member.value])), locale)
  })), ...profileFieldsForSport(sport).filter(field => metrics.isPublic(field.id)).map(field => ({
    id: field.id,
    label: tl(field.label),
    value: displaySportField(field, metrics.sportFields, locale, tl)
  })).filter(item => item.value !== '')]) : [];
  const bodyPublic = privacy.publicBodyMetrics ? metrics.bodyMetrics.filter(metric => !metrics.isHidden(metric.id) && metrics.isPublic(metric.id)) : [];
  const bioPublic = privacy.publicBiomarkers ? biomarkerDefs.filter(def => bio.isPublic(def.id)).map(def => ({
    def,
    latest: bio.recordsFor(def.id)[0]
  })).filter(item => item.latest) : [];
  const nothingPublic = !showBadges && !showStreak && sportPublic.length === 0 && !privacy.publicProgress && bodyPublic.length === 0 && bioPublic.length === 0;
  return <div className="px-2 pb-2" data-id="element-3483" data-mp="uob6mdf35esj">
      <PageHeader title={t('preview.title')} showBack data-id="element-3484" data-mp="upcksqreeheh" />

      <p className="mb-2 flex items-start gap-2 rounded-card border border-gold/35 bg-white/60 p-2 text-[12px] leading-snug text-deep" data-id="element-3485" data-mp="zmmlx3o5ghi5">
        <EyeIcon size={14} className="mt-[1px] shrink-0 text-gold-deep" aria-hidden="true" data-id="element-3486" data-mp="4bejs4nfsolv" />
        {t('preview.banner')}
      </p>

      <Card className="p-2" data-id="element-3487" data-mp="3vldaupyontn6">
        <div className="flex items-start gap-2" data-id="element-3488" data-mp="ebf9q9fta2z1">
          <Avatar src={me.avatar} name={metrics.profile.name} size={68} data-id="element-3489" data-mp="ea4567ejkr6v" />
          <div className="min-w-0 flex-1" data-id="element-3490" data-mp="9cu4fvlpct3q">
            {showStreak && <p className="bee-overline text-[9px] font-bold text-gold-deep" data-id="element-3491" data-mp="3hr2v2y4nz9t9">
                {t('preview.streak', {
              n: progress.streak
            })}
              </p>}
            <p className="mt-[2px] truncate text-[17px] font-bold text-deep" data-id="element-3492" data-mp="3cqojx77rnjr0">
              {metrics.profile.name}
            </p>
            <p className="truncate text-[12px] text-muted" data-id="element-3493" data-mp="3mq5r5t2o25p3">{metrics.profile.username}</p>
          </div>
        </div>
        <p className="mt-2 text-[13px] leading-relaxed text-deep" data-id="element-3494" data-mp="2z0309gahn5s2">{metrics.profile.bio}</p>
      </Card>

      {nothingPublic && <p className="mt-3 px-1 text-[12px] leading-snug text-muted" data-id="element-3495" data-mp="2tzxeggqwgdhw">{t('preview.hiddenAll')}</p>}

      {showBadges && <section className="mt-3" data-id="element-3496" data-mp="1eafoqfvfa9cz">
          <SectionTitle data-id="element-3497" data-mp="1ebr1342xopfp">{t('profile.achievements')}</SectionTitle>
          <ul className="flex flex-wrap gap-1" data-id="element-3498" data-mp="zm33actyo8wq">
            {me.badges.map(badge => <li key={badge} className="flex min-h-[38px] items-center gap-1 rounded-pill border border-gold/45 bg-white/70 px-2 text-[12px] font-medium text-deep" data-id="element-3499" data-mp="14kw6ncfjajj6">
                <TrophyGlyph size={16} className="text-gold-deep" aria-hidden="true" data-id="element-3500" data-mp="1xa3fb48pcpi5" />
                {t(badge)}
              </li>)}
          </ul>
        </section>}

      {sportPublic.length > 0 && <section className="mt-3" data-id="element-3501" data-mp="1nack10cbi6h0">
          <SectionTitle data-id="element-3502" data-mp="1ndgvmis444hp">{t('health.sports')}</SectionTitle>
          <div className="grid grid-cols-2 gap-2" data-id="element-3503" data-mp="7tkf9gxgh1cc">
            {sportPublic.map(item => <MetricCard key={item.id} label={item.label} value={item.value} data-id="element-3504" data-mp="bwh0zgialdki" />)}
          </div>
        </section>}

      {privacy.publicProgress && <section className="mt-3" data-id="element-3505" data-mp="29k2sw7dwel0b">
          <SectionTitle data-id="element-3506" data-mp="29kuecpds72cv">{t('preview.progress')}</SectionTitle>
          <MetricCard label={t('home.summary.lifescore')} value={t('preview.progressValue', {
        n: average(history)
      })} data-id="element-3507" data-mp="24st57cbwtrr2" />
        </section>}

      {bodyPublic.length > 0 && <section className="mt-3" data-id="element-3508" data-mp="1vsxqwyn25gmt">
          <SectionTitle data-id="element-3509" data-mp="1vtzd9z9ixpju">{t('preview.body')}</SectionTitle>
          <div className="grid grid-cols-2 gap-2" data-id="element-3510" data-mp="1qss5i07dsqd6">
            {bodyPublic.map(metric => <MetricCard key={metric.id} label={t(`metrics.${metric.id}`)} value={formatMetric(metric.kind, metric.value, metrics.unitSystem)} data-id="element-3511" data-mp="1ltyt6b3tz5el" />)}
          </div>
        </section>}

      {bioPublic.length > 0 && <section className="mt-3" data-id="element-3512" data-mp="3b9enwlh0coky">
          <SectionTitle data-id="element-3513" data-mp="3bag8phntspfv">{t('preview.biomarkers')}</SectionTitle>
          <div className="grid grid-cols-2 gap-2" data-id="element-3514" data-mp="3jqfbxresq48q">
            {bioPublic.map(({
          def,
          latest
        }) => {
          const unit = bio.unitFor(def.id);
          const shown = convertLabValue(latest!.value, def.canonicalUnit, unit, def.factors);
          return <MetricCard key={def.id} label={tl(def.name)} value={`${formatLabValue(shown, def.decimals[unit] ?? 0, locale)} ${unit}`} data-id="element-3515" data-mp="1befchmcik0v" />;
        })}
          </div>
        </section>}

      <Button variant="outline" size="lg" fullWidth className="mt-3" onClick={() => navigate('/perfil/editar')} data-id="element-3516" data-mp="39r7xf0wbs3k1">
        {t('preview.edit')}
      </Button>
    </div>;
}